import { formatDistanceToNow } from 'date-fns';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Radio,
  Router,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ManagedSimCard } from '../types';

export type TelnyxSortKey = 'iccid' | 'status' | 'assignment' | 'usage' | 'last_checked';

interface TelnyxInventoryTableProps {
  simCards: ManagedSimCard[];
  sortKey: TelnyxSortKey;
  sortDirection: 1 | -1;
  onSort: (key: TelnyxSortKey) => void;
}

interface SortableHeaderProps {
  label: string;
  value: TelnyxSortKey;
  activeValue: TelnyxSortKey;
  direction: 1 | -1;
  onSort: (key: TelnyxSortKey) => void;
  className?: string;
}

function SortableHeader({
  label,
  value,
  activeValue,
  direction,
  onSort,
  className,
}: SortableHeaderProps) {
  const active = value === activeValue;
  const Icon = active ? (direction === 1 ? ArrowUp : ArrowDown) : ArrowUpDown;

  return (
    <TableHead
      className={`cursor-pointer select-none hover:bg-muted/50 ${className || ''}`}
      onClick={() => onSort(value)}
    >
      <div className={`flex items-center gap-1 ${className?.includes('text-right') ? 'justify-end' : ''}`}>
        {label}
        <Icon className={`h-3.5 w-3.5 ${active ? '' : 'text-muted-foreground/50'}`} />
      </div>
    </TableHead>
  );
}

function formatIccid(iccid: string): string {
  return iccid ? iccid.replace(/(.{4})/g, '$1 ').trim() : '-';
}

function formatUsage(bytes: number | undefined): string {
  if (!bytes) return '0 MB';
  const megabytes = bytes / (1024 * 1024);
  if (megabytes < 1) return `${(megabytes * 1024).toLocaleString(undefined, { maximumFractionDigits: 2 })} KB`;
  if (megabytes < 1024) return `${megabytes.toLocaleString(undefined, { maximumFractionDigits: 2 })} MB`;
  return `${(megabytes / 1024).toLocaleString(undefined, { maximumFractionDigits: 2 })} GB`;
}

function statusClasses(status: string): string {
  if (status === 'enabled') return 'bg-green-100 text-green-800 hover:bg-green-100';
  if (status === 'disabled') return 'bg-gray-100 text-gray-700 hover:bg-gray-100';
  if (status === 'standby') return 'bg-amber-100 text-amber-800 hover:bg-amber-100';
  if (status === 'data_limit_exceeded' || status === 'unauthorized_imei') {
    return 'bg-red-100 text-red-800 hover:bg-red-100';
  }
  return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
}

export function TelnyxInventoryTable({
  simCards,
  sortKey,
  sortDirection,
  onSort,
}: TelnyxInventoryTableProps) {
  return (
    <Card>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader
                label="ICCID"
                value="iccid"
                activeValue={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortableHeader
                label="Status"
                value="status"
                activeValue={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortableHeader
                label="Assignment"
                value="assignment"
                activeValue={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
              <SortableHeader
                label="Usage"
                value="usage"
                activeValue={sortKey}
                direction={sortDirection}
                onSort={onSort}
                className="text-right"
              />
              <SortableHeader
                label="Last Sync"
                value="last_checked"
                activeValue={sortKey}
                direction={sortDirection}
                onSort={onSort}
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {simCards.map((sim) => {
              const device = sim.gateway ?? sim.transmitter;
              const isGateway = Boolean(sim.gateway);
              return (
                <TableRow key={sim._id}>
                  <TableCell className="font-mono text-sm">{formatIccid(sim.iccid)}</TableCell>
                  <TableCell>
                    <Badge className={statusClasses(sim.status)}>
                      {sim.status.replace(/_/g, ' ')}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {device ? (
                      <div className="flex items-center gap-2">
                        {isGateway ? (
                          <Router className="h-4 w-4 text-green-600" />
                        ) : (
                          <Radio className="h-4 w-4 text-green-600" />
                        )}
                        <div className="flex flex-col">
                          <span className="text-sm font-medium">
                            {device.name || device.customid || device.dev_id || 'Linked device'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {isGateway ? 'Gateway' : 'Transmitter'}
                            {device.dev_id ? ` · ${device.dev_id}` : ''}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <span className="text-sm text-orange-500">Unassigned</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    {formatUsage(sim.usage?.this_cycle)}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {sim.last_checked
                      ? formatDistanceToNow(new Date(sim.last_checked), { addSuffix: true })
                      : 'Never'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
